"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import CloseIcon from "@/components/_icons/closeIcon";
import { ImageModalCloseButton } from "@/components/_icons/imageModalCloseButton";
import { getServiceCartItems } from "./serviceSummary";
import DisableOverlay from "@/components/disableOverlay/disableOverlay";
import { ModalModifierSelection } from "./modalModifierSelection";

export const ServiceModalModifierSelection = ({
  isCheckoutEnabled,
  merchantName,
  onRequestClose,
  productImage,
  service,
  formatDuration,
  onShowEmployeeSelection,
  modifiers,
  selectedModifiers,
  setSelectedModifiers,
  menu,
  setFinalPrice,
  isAcceptingAppointments,
  merchantPreferences,
  merchantColor,
}: any) => {
  const [serviceCartCount, setServiceCartCount] = useState(0);

  useEffect(() => {
    const cartItems = getServiceCartItems();
    setServiceCartCount(cartItems.length);
  }, []);

  const filteredModifierGroups = menu.modifierGroups
    ? Object.values(menu.modifierGroups).filter((group: any) =>
        service.modifierGroupIds.includes(group.id)
      )
    : [];

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    const modifier = modifiers.find((mod: any) => mod.id === modifierId);
    if (!modifier) {
      return;
    }

    const currentSelections = selectedModifiers[groupId] || [];
    const group: any = filteredModifierGroups.find(
      (g: any) => g.id === groupId
    );

    if (!group) return;

    if (isSingleSelection) {
      setSelectedModifiers({
        ...selectedModifiers,
        [groupId]: modifier,
      });
    } else {
      const isSelected = currentSelections.some(
        (mod: any) => mod.id === modifierId
      );

      if (!isSelected && currentSelections.length >= group.maxAllowed) {
        return;
      }

      const newSelections = isSelected
        ? currentSelections.filter((mod: any) => mod.id !== modifierId)
        : [...currentSelections, modifier];

      setSelectedModifiers({
        ...selectedModifiers,
        [groupId]: newSelections,
      });
    }
  };

  const validateModifierSelection = () => {
    return filteredModifierGroups.every((group: any) => {
      const selected = selectedModifiers[group.id] || [];
      const isGroupValid =
        group.minRequired === 0 ||
        (Array.isArray(selected)
          ? selected.length >= group.minRequired
          : !!selected);
      return isGroupValid;
    });
  };
  const isValidSelection = validateModifierSelection();
  let disableBookButton =
    service?.available === false ||
    !isValidSelection ||
    !isAcceptingAppointments;

  return (
    <div className="relative bg-white sm:rounded-lg shadow-lg w-full max-w-full flex flex-col sm:flex-row-reverse sm:w-auto mx-auto h-[100%] sm:h-[576px]  overflow-y-hidden">
      <button
        onClick={onRequestClose}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 z-10"
      >
        {productImage ? <ImageModalCloseButton /> : <CloseIcon />}
      </button>
      {productImage && (
        <div className="flex flex-1 flex-shrink-0 sm:w-[450px] sm:h-[576px]">
          <Image
            width={450}
            height={576}
            alt={service.name || "service name"}
            src={productImage.source}
            className="object-cover object-center w-full h-full"
          />
        </div>
      )}

      <div
        className={`flex flex-1 flex-col sm:min-h-[260px] w-full ${
          productImage ? "sm:w-[450px]" : "sm:w-[530px]"
        } relative md:h-full sm:h-auto overflow-y-hidden`}
      >
        <DisableOverlay
          merchantName={merchantName}
          isCheckoutEnabled={isCheckoutEnabled}
        >
          <div className="flex flex-col px-6 pt-6">
            <h2 className="pt-4 text-4xl text-[32px] font-medium text-[#373737] whitespace-nowrap overflow-hidden text-ellipsis">
              {service.name}
            </h2>
            <p className="text-[15px] font-medium pt-4">
              from ${(service.price / 100).toFixed(2)}{" "}
              {service.serviceItem && (
                <span className="">
                  &nbsp;&bull;&nbsp;{" "}
                  {formatDuration(service.serviceItem.duration) || "0 min"}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-1 flex-col px-6 overflow-y-scroll">
            <div className="py-4">
              <div className="text-gray-500 break-words whitespace-pre-wrap">
                {service.description && <p>{service.description}</p>}
              </div>
            </div>
            <ModalModifierSelection
              {...{
                filteredModifierGroups,
                modifiers,
                selectedModifiers,
                handleModifierChange,
                merchantColor,
              }}
            />
          </div>
          <button
            style={{
              backgroundColor: disableBookButton ? "#C7C7C7" : merchantColor,
            }}
            className="flex w-full h-[64px] bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium"
            onClick={() => {
              setFinalPrice(getPrice(service, selectedModifiers));
              onShowEmployeeSelection();
            }}
            disabled={disableBookButton}
          >
            {service?.available === false || !isAcceptingAppointments ? (
              <div className="text-[#373737] text-[15px]">
                <p>Sorry, this service is currently unavailable. </p>
              </div>
            ) : (
              <div className="flex w-full h-full flex-row px-6 items-center justify-between">
                <p>Book&nbsp;</p>
                <p>
                  ${(getPrice(service, selectedModifiers) / 100).toFixed(2)}
                </p>
              </div>
            )}
          </button>
        </DisableOverlay>
      </div>
    </div>
  );
};

export const getPrice = (item: any, selectedModifiers: any) => {
  return (
    item.price +
    Object.values(selectedModifiers)
      .flat()
      .reduce((total: number, modifier: any) => {
        return total + (modifier ? modifier.price : 0);
      }, 0)
  );
};
